import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { FileText, Home, LogOut, Plus, Clock, CheckCircle, XCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quotations');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [quotesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/quotations/my-quotations`, { headers }),
        axios.get(`${API}/projects/my-projects`, { headers })
      ]);
      setQuotations(quotesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'approved' || status === 'completed') return <CheckCircle className="w-5 h-5" />;
    if (status === 'rejected') return <XCircle className="w-5 h-5" />;
    return <Clock className="w-5 h-5" />;
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_9ca81a2a-0a4d-4b76-b02e-3997c1e64651/artifacts/ypha2bz9_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20260309130435_160_131.png" 
                alt="ALIGCUS Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-4">
              <div className="text-right mr-4">
                <p className="text-sm text-gray-600">Welcome back,</p>
                <p className="font-semibold text-gray-900">{user?.full_name}</p>
              </div>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Card */}
        <Card className="mb-8 bg-gradient-to-r from-red-600 to-red-700 text-white border-0">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">Customer Dashboard</h1>
                <p className="text-red-100">Manage your ADU projects and quotations</p>
              </div>
              <Link to="/quote">
                <Button className="bg-white text-red-600 hover:bg-gray-100 font-semibold">
                  <Plus className="w-5 h-5 mr-2" />
                  New Quote
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Quotations</p>
                  <p className="text-3xl font-bold text-gray-900">{quotations.length}</p>
                </div>
                <FileText className="w-12 h-12 text-red-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Projects</p>
                  <p className="text-3xl font-bold text-gray-900">{projects.filter(p => p.status !== 'completed').length}</p>
                </div>
                <Home className="w-12 h-12 text-red-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="text-3xl font-bold text-gray-900">{projects.filter(p => p.status === 'completed').length}</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('quotations')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'quotations'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quotations ({quotations.length})
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'projects'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Projects ({projects.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Quotations Tab */}
        {activeTab === 'quotations' && (
          <div className="space-y-4">
            {quotations.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Quotations Yet</h3>
                  <p className="text-gray-600 mb-6">Get started by requesting a quote for your ADU project</p>
                  <Link to="/quote">
                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                      <Plus className="w-5 h-5 mr-2" />
                      Request a Quote
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              quotations.map((quote) => (
                <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 mr-3">
                            {quote.bedrooms} Bed / {quote.bathrooms} Bath • {quote.square_footage} sq ft
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(quote.status)}`}>
                            {quote.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">{quote.property_address}, {quote.property_city}, {quote.property_state}</p>
                        <p className="text-sm text-gray-500 mb-3">Package: {quote.package_type.charAt(0).toUpperCase() + quote.package_type.slice(1)}</p>
                        <div className="flex items-center space-x-6 text-sm">
                          <span className="text-gray-600">Created: {new Date(quote.created_at).toLocaleDateString()}</span>
                          {quote.additional_options && quote.additional_options.length > 0 && (
                            <span className="text-gray-600">Options: {quote.additional_options.length}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600 mb-1">Estimated Cost</p>
                        <p className="text-3xl font-bold text-red-600">${quote.total_price.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 mt-1">${quote.price_per_sqft}/sq ft</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            {projects.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Projects Yet</h3>
                  <p className="text-gray-600 mb-6">Your approved quotations will become projects</p>
                </CardContent>
              </Card>
            ) : (
              projects.map((project) => (
                <Card key={project.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center mb-2">
                          <h3 className="text-xl font-semibold text-gray-900 mr-3">{project.customer_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                            {project.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">Started: {new Date(project.start_date).toLocaleDateString()}</p>
                        {project.estimated_completion && (
                          <p className="text-sm text-gray-500">Est. Completion: {new Date(project.estimated_completion).toLocaleDateString()}</p>
                        )}
                      </div>
                    </div>

                    {/* Project Timeline */}
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-gray-900 mb-3">Project Timeline</h4>
                      <div className="flex items-center space-x-2">
                        {['planning', 'permits', 'construction', 'inspection', 'completed'].map((stage, idx) => {
                          const isActive = ['planning', 'permits', 'construction', 'inspection', 'completed'].indexOf(project.status) >= idx;
                          return (
                            <React.Fragment key={stage}>
                              <div className={`flex-1 h-2 rounded ${isActive ? 'bg-red-600' : 'bg-gray-200'}`} />
                              {idx < 4 && <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-red-600' : 'bg-gray-200'}`} />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-600">
                        <span>Planning</span>
                        <span>Permits</span>
                        <span>Construction</span>
                        <span>Inspection</span>
                        <span>Complete</span>
                      </div>
                    </div>

                    {/* Updates */}
                    {project.updates && project.updates.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Recent Updates</h4>
                        <div className="space-y-2">
                          {project.updates.slice(-3).reverse().map((update, idx) => (
                            <div key={idx} className="flex items-start bg-gray-50 p-3 rounded">
                              <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5" />
                              <div className="flex-1">
                                <p className="font-medium text-sm text-gray-900">{update.title}</p>
                                <p className="text-xs text-gray-600 mt-1">{update.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
